import { ActionIcon, Grid, TextInput, useMantineTheme } from '@mantine/core'
import { sortByKey } from '@semios/app-platform-common'
import { IconSearch } from 'components/icons/IconSearch'
import { PropertySelect } from 'components/PropertySelect/PropertySelect'
import { translate } from 'i18n/i18n'
import { debounce } from 'lodash'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { useIsWideScreen } from 'utils/useIsWideScreen'
import { EquipmentStatusMultiSelect } from '../../../EquipmentStatusMultiSelect/EquipmentStatusMultiSelect'
import { EquipmentTypesAndDevicesMultiSelect } from '../../../EquipmentTypesAndDevicesMultiSelect/EquipmentTypesAndDevicesMultiSelect'
import { TNodeStatus } from '../../../types'
import type { TFilters } from '../EquipmentStatus'
import { InsectMultiSelect } from './InsectMultiSelect/InsectMultiSelect'
import { IssueTypeMultiSelect } from './IssueTypeMultiSelect/IssueTypeMultiSelect'

type FiltersProps = {
  selectedPropertyId: number | null
  issueTypes: Record<number, string>
  filters: TFilters
  onFilterChange: (filters: Partial<TFilters>) => void
  onPropertySelect: (propertyId: number | null) => void
}

const FiltersWithNoMemoization: React.FC<FiltersProps> = ({
  selectedPropertyId,
  issueTypes,
  filters,
  onFilterChange,
  onPropertySelect,
}) => {
  const theme = useMantineTheme()
  const isWideScreen = useIsWideScreen()
  const properties = fieldAssetStore.useSelector((s) => s?.properties ?? {})
  // Store input value in local state because of the debounced search
  const [searchInputValue, setSearchInputValue] = useState(filters.searchKeywords)

  const propertiesData = useMemo(
    () =>
      Object.entries(properties)
        .map(([propertyId, property]) => ({
          label: property.propertyName,
          value: propertyId,
        }))
        .sort(sortByKey('label')),
    [properties],
  )

  const debouncedSearch = useCallback(
    debounce((searchKeywords: string) => onFilterChange({ searchKeywords }), 300),
    [onFilterChange],
  )

  // Update local value when filters change
  useEffect(() => {
    setSearchInputValue(filters.searchKeywords)
  }, [filters.searchKeywords])

  // Trigger search when input value changes
  useEffect(() => {
    debouncedSearch(searchInputValue)
  }, [searchInputValue, debouncedSearch])

  const colStyle: React.CSSProperties = {
    paddingBottom: 0,
  }

  return (
    <div css={{ backgroundColor: theme.colors.grey[0], padding: 12, flex: '0 0 auto' }}>
      <Grid styles={{ inner: { marginBottom: 0 } }}>
        <Grid.Col span={isWideScreen ? 6 : 12} style={colStyle}>
          <PropertySelect
            clearable={false}
            data={propertiesData}
            onChange={onPropertySelect}
            selectedPropertyId={selectedPropertyId}
          />
        </Grid.Col>

        <Grid.Col span={isWideScreen ? 6 : 12} style={colStyle}>
          <TextInput
            placeholder={translate.phrases.placeholder('Search name')}
            value={searchInputValue}
            onChange={(event) => setSearchInputValue(event.currentTarget.value)}
            rightSection={
              <ActionIcon>
                <IconSearch />
              </ActionIcon>
            }
          />
        </Grid.Col>

        <Grid.Col span={isWideScreen ? 6 : 12} style={colStyle}>
          <EquipmentTypesAndDevicesMultiSelect
            selectedEquipmentTypes={filters.equipmentTypes}
            selectedEquipmentDevices={filters.deviceTypes}
            onChange={(equipmentTypes, deviceTypes) => onFilterChange({ equipmentTypes, deviceTypes })}
          />
        </Grid.Col>

        {filters.equipmentTypes.includes('trap') && (
          <Grid.Col span={isWideScreen ? 6 : 12} style={colStyle}>
            <InsectMultiSelect
              selectedIds={filters.insectIds}
              onChange={(insectIds) => onFilterChange({ insectIds })}
            />
          </Grid.Col>
        )}

        <Grid.Col span={isWideScreen ? 6 : 12} style={colStyle}>
          <EquipmentStatusMultiSelect
            selectedStatuses={filters.statuses}
            onChange={(statuses) => onFilterChange({ statuses })}
          />
        </Grid.Col>

        {filters.statuses.includes(TNodeStatus.ERROR) && (
          <Grid.Col span={isWideScreen ? 6 : 12} style={colStyle}>
            <IssueTypeMultiSelect
              issueTypes={issueTypes}
              selectedIds={filters.issueTypeIds}
              onChange={(issueTypeIds) => onFilterChange({ issueTypeIds })}
            />
          </Grid.Col>
        )}
      </Grid>
    </div>
  )
}

export const Filters = memo(FiltersWithNoMemoization)
